// Read-only public data: https://developers.deriv.com/docs/data/ticks-history/
export const ENDPOINT = 'wss://api.derivws.com/trading/v1/options/ws/public';
export const DERIV_SYMBOLS = Object.freeze({ XAUUSD: 'frxXAUUSD', GBPJPY: 'frxGBPJPY', GBPUSD: 'frxGBPUSD', US30: 'OTC_DJI', BTCUSD: 'cryBTCUSD' });
export const GRANULARITY = Object.freeze({ M1: 60, M5: 300, M15: 900, M30: 1800, H1: 3600, H4: 14400, D1: 86400 });
export const WTI_UNAVAILABLE = 'WTI belum disediakan oleh sumber data ini. Pilih XAUUSD, GBPJPY, GBPUSD, US30 atau BTCUSD.';
export function providerSymbol(symbol) {
  if (!Object.hasOwn(DERIV_SYMBOLS, symbol)) throw new Error(symbol === 'WTI' ? WTI_UNAVAILABLE : 'Unsupported pair.');
  return DERIV_SYMBOLS[symbol];
}

// Bounded, request-scoped sockets: never reuse another Cloudflare request's I/O.
export function requestDeriv(payload, { socketFactory = () => new WebSocket(ENDPOINT), timeoutMs = 6500, signal } = {}) {
  return new Promise((resolve, reject) => {
    let socket, settled = false;
    const finish = (error, value) => {
      if (settled) return;
      settled = true; clearTimeout(timer);
      signal?.removeEventListener('abort', abort);
      try { socket?.close(); } catch {}
      if (error) reject(error); else resolve(value);
    };
    const abort = () => finish(new DOMException('Aborted', 'AbortError'));
    const timer = setTimeout(() => finish(new Error('Market data connection timed out.')), timeoutMs);
    if (signal?.aborted) { abort(); return; }
    signal?.addEventListener('abort', abort, { once: true });
    Promise.resolve().then(socketFactory).then(ws => {
      socket = ws;
      if (settled) { socket.close(); return; }
      const send = () => { if (!settled) { try { socket.send(JSON.stringify({ ...payload, req_id: 1 })); } catch (error) { finish(error); } } };
      socket.addEventListener('message', event => {
        try {
          const value = JSON.parse(event.data);
          if (value.req_id !== 1) return;
          if (value.error) finish(new Error('Market data unavailable: ' + value.error.code));
          else finish(null, value);
        } catch { finish(new Error('Invalid market data response.')); }
      });
      socket.addEventListener('error', () => finish(new Error('Market data connection failed.')));
      socket.addEventListener('close', () => finish(new Error('Market data connection closed.')));
      if (socket.readyState === 1) send();
      else socket.addEventListener('open', send, { once: true });
    }).catch(error => finish(error));
  });
}
export async function cloudflareSocket() {
  return new WebSocket(ENDPOINT);
}
const epochMs = (epoch, now) => {
  if (!Number.isSafeInteger(epoch)) throw new Error('Invalid market timestamp.');
  const time = epoch * 1000;
  if (!Number.isSafeInteger(time) || time <= 0 || time > now + 10000) throw new Error('Invalid market timestamp.');
  return time;
};
const price = value => {
  if (!Number.isFinite(value) || value <= 0) throw new Error('Invalid market price.');
  return value;
};
export function decodeDerivCandles(value, now = Date.now()) {
  if (!Array.isArray(value.candles) || value.candles.length < 2) throw new Error('Empty market history.');
  let previous = 0;
  return value.candles.map(row => {
    const time = epochMs(row.epoch, now);
    const [open, high, low, close] = [row.open, row.high, row.low, row.close].map(price);
    if (time <= previous || low > Math.min(open, close) || high < Math.max(open, close)) throw new Error('Invalid market candle.');
    previous = time;
    return { time, open, high, low, close, volume: 0 };
  });
}
export function decodeDerivTicks(value, now = Date.now()) {
  const history = value.history;
  if (!history || !Array.isArray(history.times) || !Array.isArray(history.prices) || history.times.length !== history.prices.length) throw new Error('Invalid tick history.');
  let previous = 0;
  return history.times.map((epoch, i) => {
    const time = epochMs(epoch, now);
    if (time < previous) throw new Error('Unordered tick history.');
    previous = time;
    return { time, price: price(history.prices[i]) };
  });
}
export async function loadDerivCandles(symbol, tf, options) {
  const underlying = providerSymbol(symbol);
  if (!Object.hasOwn(GRANULARITY, tf)) throw new Error('Unsupported timeframe.');
  const value = await requestDeriv({ ticks_history: underlying, style: 'candles', granularity: GRANULARITY[tf], count: tf === 'H4' || tf === 'D1' ? 300 : 600, end: 'latest', adjust_start_time: 1 }, options);
  const now = Date.now();
  return { source: 'Deriv', symbol, priceType: 'quote', sessionOffsetMinutes: 0, timeframe: tf, fetchedAt: new Date(now).toISOString(), candles: decodeDerivCandles(value, now) };
}
export async function loadDerivQuotes(symbol, since = 0, options) {
  const underlying = providerSymbol(symbol), startedAt = Date.now();
  const start = Math.floor((startedAt - 120000) / 1000);
  const value = await requestDeriv({ ticks_history: underlying, style: 'ticks', start, count: 1000, end: 'latest' }, options);
  const now = Date.now(), ticks = decodeDerivTicks(value, now);
  const coverageFrom = Math.max(start * 1000, ticks[0]?.time || startedAt);
  return { source: 'Deriv', symbol, priceType: 'quote', sessionOffsetMinutes: 0, fetchedAt: new Date(now).toISOString(), quote: ticks.at(-1) || null, coverageFrom, ticks: ticks.filter(tick => !since || tick.time >= since) };
}
